import i18n from 'i18n/config';
import { getFirst, getLastItem } from './array';

const languageToLocaleString: Record<string, string> = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es',
};

export function isNumericText(text: string): boolean {
  return /^\d*$/.test(text);
}

export function removeNonNumericChars(text: string): string {
  return text.replace(/[^0-9]/g, '');
}

export function truncateText(text: string, maxSize: number): string {
  if (text.length <= maxSize) return text;

  return `${text.slice(0, maxSize)}…`;
}

export function getInitials(name: string): string {
  const names = name.split(' ');

  const [firstNameLetter, lastNameLetter] = [getFirst, getLastItem].map((cb) =>
    getFirst((cb(names) as string)?.split('')),
  );

  if (names.length === 1) return firstNameLetter?.toUpperCase() ?? '';

  return `${firstNameLetter}${lastNameLetter}`.toUpperCase();
}

export function normalizeText(text: string): string {
  const lower = String(text).trim().toLocaleLowerCase(['pt-BR', 'pt']);

  const cleaned = lower
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[éèêẽë]/g, 'e')
    .replace(/[íìîĩ]/g, 'i')
    .replace(/[òóôõ]/g, 'o')
    .replace(/[úùûũ]/g, 'u')
    .replace(/[ç]/g, 'c')
    .replace(/[ñ]/g, 'n');

  return cleaned;
}

export function normalizeSneakyText(text: string): string {
  const parsed = normalizeText(text)
    .replace('0', 'o')
    .replace('1', 'i')
    .replace('3', 'e')
    .replace('4', 'a')
    .toLowerCase();

  return parsed;
}

/**
 *
 * @param text The reference text
 * @param testTerm What we want to know if its substring
 * @returns true/false is substring
 *
 * IMPORTANT: This normalizes both entries, meaning `today` has `DaY` as substring
 * as well as não = nao
 */
export function isSubstring(subString: string, text: string): boolean {
  return normalizeText(text).includes(normalizeText(subString));
}

/**
 * Compares if the text is the same, excluding accents/capitalization
 */
export function isSameText(text: string, testTerm: string): boolean {
  return normalizeText(text) === normalizeText(testTerm);
}

export function getFirstWord(name: string): string {
  return getFirst(name.trim().split(' '));
}

export function getFirstAndLastName(name: string): string {
  const names = name.split(' ');

  const [firstName, lastName] = [getFirst, getLastItem].map((cb) => cb(names));

  if (names.length === 1) return `${firstName}`;

  return `${firstName} ${lastName}`;
}

export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function formatCurrency(value: string | number, currency?: string): string {
  const numeric = typeof value === 'string' ? Number(value) : value;

  const formatted = new Intl.NumberFormat(languageToLocaleString[i18n.language] ?? 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);

  if (!currency) return formatted;

  return `${currency} ${formatted}`;
}

/**
 * Replaces problematic chars to properly display the name
 */
export function fixFileName(name: string) {
  return name.replace(/_/g, ' ');
}

export function fileNameToReadable(name: string) {
  const [, ...nameParts] = name.split('.').reverse();

  return nameParts.reverse().join(' ').replace(/[-_/]/g, ' ').replace(/\s+/g, ' ');
}
